/**
 * NotificationPolicyPage — Admin → Notifications. The Notification Control
 * Panel: one tab per policy role (org-wide default + each staff role), a
 * checkbox grid of event × channel. Policy is FINAL for everyone — there are
 * no employee self-service overrides; the per-device browser-push toggle on
 * the Notifications page only registers a device. Backed by
 * /v1/admin/notification-policy (admin.access, i.e. CEO/GM by default).
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ORG_POLICY_ROLE, ROLE_LABELS, type StaffRole } from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../../lib/auth-context.js';
import { httpError } from '../../lib/http-error.js';
import { clsx } from 'clsx';

interface EventRow { key: string; label: string }
interface ChannelCol { key: string; label: string }
interface OverrideRow {
  eventType: string;
  role: string;
  channel: string;
  enabled: boolean;
  updatedById: string | null;
  updatedAt: string;
}
interface PolicyData {
  events: EventRow[];
  channels: ChannelCol[];
  roles: string[];
  defaults: Record<string, Record<string, boolean>>;
  overrides: OverrideRow[];
  matrix: Record<string, Record<string, Record<string, boolean>>>;
}

type Matrix = Record<string, Record<string, Record<string, boolean>>>;

export function NotificationPolicyPage() {
  const [role, setRole] = useState<string>(ORG_POLICY_ROLE);
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const query = useQuery({
    queryKey: ['admin-notification-policy'],
    queryFn: async () => {
      const res = await fetch('/v1/admin/notification-policy', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: PolicyData };
      return body.data;
    },
  });

  useEffect(() => {
    if (query.data) {
      setMatrix(query.data.matrix);
      setOverrides(query.data.overrides);
    }
  }, [query.data]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const overrideMap = useMemo(() => {
    const m = new Map<string, OverrideRow>();
    for (const o of overrides) m.set(`${o.role}:${o.eventType}:${o.channel}`, o);
    return m;
  }, [overrides]);

  async function toggle(eventType: string, channel: string) {
    if (!matrix) return;
    const current = matrix[role]?.[eventType]?.[channel] ?? false;
    const next = !current;
    const cellKey = `${role}:${eventType}:${channel}`;

    setMatrix((m) => (m ? { ...m, [role]: { ...m[role], [eventType]: { ...m[role]![eventType], [channel]: next } } } : m));
    setPending((p) => new Set(p).add(cellKey));
    try {
      const res = await fetch('/v1/admin/notification-policy', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ eventType, role, channel, enabled: next }),
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { override: OverrideRow } };
      setOverrides((list) => {
        const rest = list.filter((o) => !(o.role === role && o.eventType === eventType && o.channel === channel));
        return [...rest, body.data.override];
      });
      const roleLabel = role === ORG_POLICY_ROLE ? 'everyone' : (ROLE_LABELS[role as StaffRole] ?? role);
      setToast({ kind: 'ok', text: `${next ? 'Enabled' : 'Disabled'} ${channel} for ${eventType} (${roleLabel})` });
    } catch (err) {
      setMatrix((m) => (m ? { ...m, [role]: { ...m[role], [eventType]: { ...m[role]![eventType], [channel]: current } } } : m));
      setToast({ kind: 'error', text: err instanceof Error ? err.message : 'Update failed — reverted' });
    } finally {
      setPending((p) => {
        const copy = new Set(p);
        copy.delete(cellKey);
        return copy;
      });
    }
  }

  if (query.error) {
    return <div className="bg-bg-panel border border-border rounded-xl p-8 text-center text-rose-400 text-sm">{(query.error as Error).message}</div>;
  }
  if (query.isLoading || !matrix) {
    return <div className="bg-bg-panel border border-border rounded-xl p-8 text-center text-muted text-sm">Loading notification policy…</div>;
  }

  const data = query.data!;
  const roleTabLabel = (r: string) => (r === ORG_POLICY_ROLE ? 'Everyone (org default)' : (ROLE_LABELS[r as StaffRole] ?? r));

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">
        Firm-wide notification policy — final for everyone, no per-user overrides. Dotted cells are saved overrides of
        the built-in defaults. Role tabs override the Everyone default. Browser push only reaches devices where the
        user turned it on themselves.
      </p>

      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {data.roles.map((r) => (
          <button
            key={r}
            onClick={() => setRole(r)}
            className={clsx(
              'px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap',
              role === r ? 'border-accent text-white' : 'border-transparent text-muted hover:text-white',
            )}
          >
            {roleTabLabel(r)}
          </button>
        ))}
      </div>

      <div className="bg-bg-panel border border-border rounded-xl overflow-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-bg-card border-b border-border">
              <th className="px-4 py-3 text-left text-xs font-medium text-muted sticky left-0 bg-bg-card">Event</th>
              {data.channels.map((c) => (
                <th key={c.key} className="px-3 py-3 text-center text-xs font-medium text-muted whitespace-nowrap">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.events.map((event) => (
              <tr key={event.key} className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-4 py-2.5 sticky left-0 bg-bg-panel">
                  <div className="text-white">{event.label}</div>
                  <div className="text-[11px] text-muted">{event.key}</div>
                </td>
                {data.channels.map((c) => {
                  const checked = matrix[role]?.[event.key]?.[c.key] ?? false;
                  const cellKey = `${role}:${event.key}:${c.key}`;
                  const isOverride = overrideMap.has(cellKey);
                  const busy = pending.has(cellKey);
                  return (
                    <td key={c.key} className="px-3 py-2.5 text-center">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => toggle(event.key, c.key)}
                        title={`${checked ? 'Disable' : 'Enable'} ${c.label} for ${event.label} (${roleTabLabel(role)})`}
                        className={clsx(
                          'inline-flex items-center justify-center w-9 h-6 rounded-md border transition-colors disabled:opacity-50',
                          checked ? 'bg-accent/20 border-accent text-emerald-300' : 'bg-bg-card border-border text-muted',
                          isOverride && 'border-dotted',
                        )}
                      >
                        {checked ? 'On' : 'Off'}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {toast && (
        <div
          className={clsx(
            'fixed bottom-4 right-4 z-50 px-4 py-2.5 rounded-lg border text-sm shadow-2xl',
            toast.kind === 'ok'
              ? 'bg-bg-panel border-accent text-emerald-300'
              : 'bg-bg-panel border-rose-800 text-rose-300',
          )}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
